import { useEffect, useState, type FormEvent } from "react";
import { api } from "../../api";
import { aucklandLocalToUtc, utcToAucklandInput } from "../../../shared/time";
import type { EventDetail, PlanSummary, Resource, Room } from "../../../shared/types";
import { ErrorNote } from "../../ui";
import { todayKey } from "./dates";
import { messageOf } from "./chrome";

const KINDS = [
  ["gathering", "Gathering"],
  ["rehearsal", "Rehearsal"],
  ["registration", "Registration"],
  ["meeting", "Meeting"],
] as const;

const STATUSES = [
  ["confirmed", "Confirmed"],
  ["draft", "Draft"],
  ["cancelled", "Cancelled"],
] as const;

function startValue(seedDay: string | undefined, initial?: EventDetail | null): string {
  if (initial) return utcToAucklandInput(initial.startsAt);
  const day = seedDay && /^\d{4}-\d{2}-\d{2}$/.test(seedDay) ? seedDay : todayKey();
  return `${day}T10:00`;
}

export function EventForm({ initial, seedDay, onSaved, onClose }: {
  initial?: EventDetail | null;
  seedDay?: string;
  onSaved: (event: EventDetail) => void;
  onClose: () => void;
}) {
  const seededStart = startValue(seedDay, initial);
  const [title, setTitle] = useState(initial?.title ?? "");
  const [kind, setKind] = useState(initial?.kind ?? "gathering");
  const [start, setStart] = useState(seededStart);
  const [end, setEnd] = useState(initial ? utcToAucklandInput(initial.endsAt) : utcToAucklandInput(new Date(new Date(aucklandLocalToUtc(seededStart)).getTime() + 90 * 60 * 1000).toISOString()));
  const [place, setPlace] = useState(initial?.place ?? "");
  const [roomId, setRoomId] = useState(initial?.roomId ?? "");
  const [planId, setPlanId] = useState(initial?.planId ?? "");
  const [description, setDescription] = useState(initial?.description ?? "");
  const [status, setStatus] = useState(initial?.status ?? "confirmed");
  const [capacity, setCapacity] = useState(initial?.capacity == null ? "" : String(initial.capacity));
  const [resourceIds, setResourceIds] = useState<string[]>(initial?.resources.map((resource) => resource.id) ?? []);
  const [rooms, setRooms] = useState<Room[]>([]);
  const [resources, setResources] = useState<Resource[]>([]);
  const [plans, setPlans] = useState<PlanSummary[]>([]);
  const [catalogError, setCatalogError] = useState("");
  const [error, setError] = useState("");
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    let live = true;
    void Promise.all([
      api<PlanSummary[]>("/api/v1/plans"),
      api<{ rooms: Room[]; resources: Resource[] }>("/api/v1/rooms"),
    ]).then(([planRows, catalog]) => {
      if (!live) return;
      setPlans(planRows);
      setRooms(catalog.rooms);
      setResources(catalog.resources);
    }).catch((caught: unknown) => {
      if (live) setCatalogError(messageOf(caught));
    });
    return () => { live = false; };
  }, []);

  const room = rooms.find((item) => item.id === roomId);

  async function save(event: FormEvent) {
    event.preventDefault();
    setError("");
    const trimmed = title.trim();
    if (!trimmed) {
      setError("Give the event a title.");
      return;
    }
    let startsAt = "";
    let endsAt = "";
    try {
      startsAt = aucklandLocalToUtc(start);
      endsAt = aucklandLocalToUtc(end);
    } catch (caught) {
      setError(messageOf(caught));
      return;
    }
    if (new Date(endsAt) <= new Date(startsAt)) {
      setError("End time must be after the start.");
      return;
    }
    const capacityValue = capacity.trim() === "" ? null : Number(capacity);
    if (capacityValue !== null && (!Number.isInteger(capacityValue) || capacityValue < 1 || capacityValue > 10000)) {
      setError("Capacity is a whole number from 1 to 10000, or leave it blank.");
      return;
    }
    const payload = {
      title: trimmed,
      kind: kind.trim() || "gathering",
      startsAt,
      endsAt,
      place: place.trim(),
      roomId: roomId || null,
      planId: planId || null,
      description,
      status: STATUSES.some((item) => item[0] === status) ? status : "confirmed",
      capacity: capacityValue,
      resourceIds,
    };
    setSaving(true);
    try {
      const saved = initial
        ? await api<EventDetail>(`/api/v1/events/${initial.id}`, { method: "PATCH", body: JSON.stringify(payload) })
        : await api<EventDetail>("/api/v1/events", { method: "POST", body: JSON.stringify(payload) });
      onSaved(saved);
    } catch (caught) {
      setError(messageOf(caught));
    } finally {
      setSaving(false);
    }
  }

  return (
    <form className="form-grid" onSubmit={(event) => void save(event)}>
      {error && <ErrorNote>{error}</ErrorNote>}
      {catalogError && <p className="notice">Rooms could not be loaded. {catalogError}</p>}
      <div className="field">
        <label htmlFor="event-title">Title</label>
        <input id="event-title" value={title} onChange={(event) => setTitle(event.target.value)} maxLength={180} required />
      </div>
      <div className="field">
        <label htmlFor="event-kind">Kind</label>
        <select id="event-kind" value={kind} onChange={(event) => setKind(event.target.value)}>
          {KINDS.map(([value, label]) => <option key={value} value={value}>{label}</option>)}
          {!KINDS.some((item) => item[0] === kind) ? <option value={kind}>{kind}</option> : null}
        </select>
      </div>
      <div className="field">
        <label htmlFor="event-start">Starts (Auckland)</label>
        <input id="event-start" type="datetime-local" value={start} onChange={(event) => setStart(event.target.value)} required />
      </div>
      <div className="field">
        <label htmlFor="event-end">Ends (Auckland)</label>
        <input id="event-end" type="datetime-local" value={end} onChange={(event) => setEnd(event.target.value)} required />
      </div>
      <div className="field">
        <label htmlFor="event-place">Place</label>
        <input id="event-place" value={place} onChange={(event) => setPlace(event.target.value)} maxLength={180} />
      </div>
      <div className="field">
        <label htmlFor="event-room">Room</label>
        <select id="event-room" value={roomId} onChange={(event) => setRoomId(event.target.value)}>
          <option value="">No room</option>
          {roomId && !rooms.some((item) => item.id === roomId) ? <option value={roomId}>{initial?.roomName ?? "Current room"}</option> : null}
          {rooms.map((item) => <option key={item.id} value={item.id}>{item.name}{item.capacity != null ? ` · ${item.capacity}` : ""}</option>)}
        </select>
        {room?.notes ? <p className="faint">{room.notes}</p> : null}
        {room?.capacity != null ? (
          <button className="btn small ghost" type="button" onClick={() => setCapacity(String(room.capacity))}>Use room capacity ({room.capacity})</button>
        ) : null}
      </div>
      <div className="field">
        <label htmlFor="event-plan">Linked plan</label>
        <select id="event-plan" value={planId} onChange={(event) => setPlanId(event.target.value)}>
          <option value="">No plan</option>
          {planId && !plans.some((plan) => plan.id === planId) ? <option value={planId}>Linked plan</option> : null}
          {plans.map((plan) => <option key={plan.id} value={plan.id}>{plan.title}</option>)}
        </select>
        <p className="faint">Linking a plan keeps that plan from showing a second time on the calendar.</p>
      </div>
      <div className="field">
        <label htmlFor="event-status">Status</label>
        <select id="event-status" value={status} onChange={(event) => setStatus(event.target.value)}>
          {STATUSES.map(([value, label]) => <option key={value} value={value}>{label}</option>)}
        </select>
      </div>
      <div className="field">
        <label htmlFor="event-capacity">Capacity</label>
        <input id="event-capacity" inputMode="numeric" value={capacity} onChange={(event) => setCapacity(event.target.value)} placeholder="Blank for no limit" />
      </div>
      <fieldset className="field">
        <legend>Resources</legend>
        {resources.length === 0 ? <p className="faint">The inventory has no resources to book. Adding resources is not available on this API.</p> : (
          <div className="checks">
            {resources.map((resource) => (
              <label key={resource.id} className="badge" title={`${resource.kind}${resource.notes ? ` · ${resource.notes}` : ""}`}>
                <input
                  type="checkbox"
                  checked={resourceIds.includes(resource.id)}
                  onChange={() => setResourceIds((current) => current.includes(resource.id) ? current.filter((id) => id !== resource.id) : [...current, resource.id])}
                />
                {" "}{resource.name}
              </label>
            ))}
          </div>
        )}
        <p className="faint">A room or resource already booked in this time returns an error and nothing is saved.</p>
      </fieldset>
      <div className="field">
        <label htmlFor="event-description">Description</label>
        <textarea id="event-description" value={description} onChange={(event) => setDescription(event.target.value)} maxLength={8000} />
      </div>
      <div className="cluster">
        <button className="btn primary" type="submit" disabled={saving}>{saving ? "Saving…" : initial ? "Save event" : "Schedule event"}</button>
        <button className="btn ghost" type="button" onClick={onClose}>Close</button>
      </div>
    </form>
  );
}
